<template>
    <div class="automationAlerts">
        <Box
            title="Alertas de Vencimento"
            sub-title="Configure alertas inteligentes para seus documentos e receba notificações automáticas antes do vencimento."
            icon="clock"
            :variant="BOX_VARIANTS.DEFAULT"
            :actions="headerActions"
            @action-click="handleHeaderAction"
        >

            <!-- Cards de estatísticas -->
            <div class="statsSection">
                <div class="row">
                    <!-- Próximos Vencimentos -->
                    <div class="grid_col_4 grid_col_m_6 grid_col_s_12 grid_col_xs_12">
                        <div class="statCard statCard--warning">
                            <div class="statCard_header">
                                <div class="statCard_icon">
                                    <sw-icon name="clock" />
                                </div>
                                <div class="statCard_info">
                                    <span class="statCard_label">Próximos Vencimentos</span>
                                    <div class="statCard_numbers">
                                        <span class="statCard_main">{{ upcomingNotificationsCount }}</span>
                                        <span class="statCard_secondary">nos próximos 30 dias</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Documentos Vencidos -->
                    <div class="grid_col_4 grid_col_m_6 grid_col_s_12 grid_col_xs_12">
                        <div class="statCard statCard--danger">
                            <div class="statCard_header">
                                <div class="statCard_icon">
                                    <sw-icon name="warning" />
                                </div>
                                <div class="statCard_info">
                                    <span class="statCard_label">Documentos Vencidos</span>
                                    <div class="statCard_numbers">
                                        <span class="statCard_main">{{ expiredDocumentsCount }}</span>
                                        <span class="statCard_secondary">requer ação</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>

            <!-- Seção de Filtros -->
            <FilterSection
                label="Filtrar por:"
                search-placeholder="Buscar alertas..."
                :search-value="searchQuery"
                :filters="filterOptions"
                :filter-values="filterValues"
                @update:search="searchQuery = $event"
                @update:filter="updateFilter"
                @clear-filters="clearFilters"
            />

        </Box>

        <!-- Lista de alertas -->
        <div class="alertsSection" v-if="filteredAlerts.length > 0">
            <div class="row">
                <div
                    v-for="alert in filteredAlerts"
                    :key="alert.id"
                    class="grid_col_4 grid_col_m_6 grid_col_s_12 grid_col_xs_12"
                >
                    <div class="alertCard card-base">
                        <div class="card-status-bar" :class="'card-status-bar--' + getStatusClass(alert)"></div>

                        <div class="card-header">
                            <div class="alert-header-content">
                                <div class="alert-title-section">
                                    <h4>{{ alert.name }}</h4>
                                </div>
                                <div class="alert-actions">
                                    <div class="action-buttons">
                                        <button
                                            class="action-btn action-btn--edit"
                                            @click="editAlert(alert)"
                                            title="Editar alerta"
                                        >
                                            <sw-icon name="pencil" />
                                        </button>
                                        <button
                                            class="action-btn action-btn--delete"
                                            @click="deleteAlert(alert)"
                                            title="Excluir alerta"
                                        >
                                            <sw-icon name="bin" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div class="alert-description">
                                <div class="document-info">
                                    <div class="expiry-info" :class="getExpiryClass(alert)">
                                        <span>{{ formatExpiryDate(alert.expiryDate) }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="card-content">
                            <div class="alert-metrics">
                                <div class="detail-item detail-item--compact">
                                    <div class="detail-icon">
                                        <sw-icon name="clock" />
                                    </div>
                                    <div class="detail-content">
                                        <span class="detail-label">Avisar com</span>
                                        <span class="detail-value">{{ formatTriggerDays(alert.triggerDays) }}</span>
                                    </div>
                                </div>
                                <div class="detail-item detail-item--compact">
                                    <div class="detail-icon">
                                        <sw-icon name="users" />
                                    </div>
                                    <div class="detail-content">
                                        <span class="detail-label">Destinatários</span>
                                        <span class="detail-value">{{ alert.recipients.length }} pessoa(s)</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="card-footer" v-if="alert.nextNotification">
                            <div class="next-notification-info">
                                <div class="next-notification-icon">
                                    <sw-icon name="bell" />
                                </div>
                                <div class="next-notification-content">
                                    <span class="next-notification-label">Próximo alerta</span>
                                    <span class="next-notification-date">{{ formatNextNotification(alert.nextNotification) }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Estado vazio -->
        <EmptyState
            v-else
            icon="clock"
            title="Nenhum alerta encontrado"
            description="Crie seu primeiro alerta inteligente para ser notificado sobre vencimentos de documentos."
            :action-button="{
                text: 'Criar Primeiro Alerta',
                action: () => handleHeaderAction('create')
            }"
        />

        <!-- Modal de Exclusão -->
        <sw-modal
            :visible="deleteModalVisible"
            @update="deleteModalVisible = $event">

            <template #header>
                <ModalHeader
                    icon="bin"
                    title="Excluir Alerta Inteligente"
                    subtitle="Esta ação não pode ser desfeita"
                    color="red"
                />
            </template>

            <template #content>
                <div class="modalContent">
                    <p class="confirmText">
                        O alerta <strong>"{{ selectedAlert?.name }}"</strong> será removido permanentemente.
                        Você não receberá mais notificações sobre o vencimento deste documento.
                    </p>
                    <p class="warningText">
                        O documento original permanecerá seguro em sua conta, apenas o sistema de alertas será desativado.
                    </p>

                    <div class="modalActions">
                        <sw-button
                            name="Cancelar"
                            color="gray"
                            @click="closeDeleteModal"
                        />
                        <sw-button
                            name="Excluir Alerta"
                            color="red"
                            @click="confirmDelete"
                        />
                    </div>
                </div>
            </template>
        </sw-modal>

        <!-- Modal de Edição -->
        <sw-modal
            :visible="editModalVisible"
            @update="editModalVisible = $event">

            <template #header>
                <ModalHeader
                    icon="clock"
                    title="Editar Alerta Inteligente"
                    subtitle="Configure alertas automáticos de vencimento"
                    color="blue"
                />
            </template>

            <template #content>
                <AlertDetailModal
                    :alert-data="selectedAlert"
                    @close="closeModal"
                    @save="saveAlert"
                />
            </template>
        </sw-modal>

        <!-- Modal de Criação -->
        <sw-modal
            :visible="newAlertModalVisible"
            @update="newAlertModalVisible = $event">

            <template #header>
                <ModalHeader
                    icon="plus"
                    title="Novo Alerta Inteligente"
                    subtitle="Configure alertas automáticos de vencimento"
                    color="blue"
                />
            </template>

            <template #content>
                <NewAlertModal
                    @close="closeModal"
                    @save="createAlert"
                />
            </template>
        </sw-modal>
    </div>
</template>

<script setup>
    import { ref, computed, onMounted } from 'vue';

    import AlertDetailModal from '@Component/notifications/AlertDetailModal.vue';
    import NewAlertModal from '@Component/automation/NewAlertModal.vue';
    import Box from '@Component/boxs/BoxComponent.vue';
    import ModalHeader from '@Component/modals/ModalHeader.vue';
    import FilterSection from '@Component/filters/FilterSection.vue';
    import EmptyState from '@Component/empty-states/EmptyState.vue';

    import {
        BOX_VARIANTS,
        ALERT_STATUS,
        ALERT_STATUS_LABELS,
        ALERT_URGENCY_LEVELS,
        PERSON_TYPES,
        PERSON_TYPE_LABELS,
        FILE_ICONS
    } from '@/constants';
    import { mockAutomationAlerts, mockFileCategories, mockFileGroups, mockClients } from '@/data/mockData';
    import { notify } from 'sysweb-vue-components/src/global';

    // Estado
    const alerts = ref([]);
    const searchQuery = ref('');
    const editModalVisible = ref(false);
    const newAlertModalVisible = ref(false);
    const deleteModalVisible = ref(false);
    const selectedAlert = ref(null);

    // Ações do header
    const headerActions = computed(() => [
        {
            type: 'button',
            icon: 'plus',
            text: 'Novo Alerta',
            title: 'Criar novo alerta de vencimento',
            key: 'create'
        }
    ]);

    // Configuração dos filtros para o FilterSection
    const filterOptions = computed(() => {

        return [
            {
                key: 'status',
                label: 'Status',
                options: [
                    { value: '', label: 'Todos os Status' },
                    { value: 'expired', label: 'Vencidos' },
                    { value: 'expiring', label: 'A vencer' }
                ]
            }
        ];
    });

    const filterValues = ref({
        status: ''
    });

    // Computed
    const filteredAlerts = computed(() => {
        let filtered = alerts.value;

        if (searchQuery.value) {
            const searchTerm = searchQuery.value.toLowerCase();
            filtered = filtered.filter(alert => {
                const name = alert.name?.toLowerCase() || '';
                const documentType = alert.documentType?.toLowerCase() || '';
                const documentName = alert.documentName?.toLowerCase() || '';

                // Buscar também no nome da empresa se existir
                let clientName = '';
                if (alert.clientId) {
                    const client = mockClients.find(c => c.id === alert.clientId);
                    clientName = client?.name?.toLowerCase() || '';
                }

                // Buscar também na categoria se existir
                let categoryName = '';
                if (alert.category) {
                    const categoryData = mockFileGroups.find(c => c.value === alert.category);
                    categoryName = categoryData?.label?.toLowerCase() || '';
                }

                return name.includes(searchTerm) ||
                       documentType.includes(searchTerm) ||
                       documentName.includes(searchTerm) ||
                       clientName.includes(searchTerm) ||
                       categoryName.includes(searchTerm);
            });
        }

        if (filterValues.value.status) {
            if (filterValues.value.status === 'expired') {
                // Filtrar documentos já vencidos
                filtered = filtered.filter(alert => {
                    if (!alert.expiryDate) return false;
                    const expiryDate = new Date(alert.expiryDate);
                    const now = new Date();
                    return expiryDate < now;
                });
            } else if (filterValues.value.status === 'expiring') {
                // Filtrar documentos ativos (não vencidos)
                filtered = filtered.filter(alert => {
                    if (!alert.expiryDate) return false;
                    const expiryDate = new Date(alert.expiryDate);
                    const now = new Date();
                    return expiryDate >= now;
                });
            }
        }

        return filtered;
    });

    const activeAlertsCount = computed(() => {
        return alerts.value.filter(alert => isAlertActive(alert)).length;
    });

    const monitoredDocumentsCount = computed(() => {
        return alerts.value.filter(alert => isAlertActive(alert)).length;
    });

    const upcomingNotificationsCount = computed(() => {
        return alerts.value.filter(alert => {
            if (!alert.nextNotification || !isAlertActive(alert)) return false;
            const nextDate = new Date(alert.nextNotification);
            const now = new Date();
            const thirtyDaysFromNow = new Date(now.getTime() + (30 * 24 * 60 * 60 * 1000));
            return nextDate >= now && nextDate <= thirtyDaysFromNow;
        }).length;
    });

    const criticalAlertsCount = computed(() => {
        return alerts.value.filter(alert =>
            alert.urgencyLevel === ALERT_URGENCY_LEVELS.CRITICAL && isAlertActive(alert)
        ).length;
    });

    const expiredDocumentsCount = computed(() => {
        return alerts.value.filter(alert => {
            if (!alert.expiryDate) return false;
            const expiryDate = new Date(alert.expiryDate);
            const now = new Date();
            return expiryDate < now && isAlertActive(alert);
        }).length;
    });

    // Métodos de filtro
    const updateFilter = ({ key, value }) => {
        filterValues.value[key] = value;
    };

    const clearFilters = () => {
        filterValues.value = {
            status: ''
        };
        searchQuery.value = '';
    };

    // Métodos de utilidade - todos os alertas são ativos
    const isAlertActive = (alert) => {
        return true; // Sempre ativo, não há status inativo
    };

    const getStatusClass = (alert) => {
        // Verificar se o documento está vencido baseado na data
        if (alert.expiryDate) {
            const expiryDate = new Date(alert.expiryDate);
            const now = new Date();

            if (expiryDate < now) {
                return 'urgent'; // Documento vencido
            }

            // Verificar se está próximo do vencimento (próximos 7 dias)
            const sevenDaysFromNow = new Date(now.getTime() + (7 * 24 * 60 * 60 * 1000));
            if (expiryDate <= sevenDaysFromNow) {
                return 'waiting'; // Próximo do vencimento
            }
        }

        // Fallback para status do alerta
        const statusClasses = {
            [ALERT_STATUS.ACTIVE]: 'active',
            [ALERT_STATUS.URGENT]: 'urgent'
        };
        return statusClasses[alert.status] || 'active';
    };

    const getStatusLabel = (alert) => {
        return ALERT_STATUS_LABELS[alert.status] || 'Desconhecido';
    };

    const getDocumentIcon = (documentType) => {
        return FILE_ICONS[documentType] || 'file-text';
    };

    const getExpiryClass = (alert) => {
        if (!alert.expiryDate) return 'no-expiry';

        const expiryDate = new Date(alert.expiryDate);
        const now = new Date();
        const diffTime = expiryDate.getTime() - now.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays < 0) {
            return 'expiry-critical'; // Vencido
        } else if (diffDays <= 7) {
            return 'expiry-warning'; // Próximo do vencimento (7 dias)
        } else if (diffDays <= 30) {
            return 'expiry-safe'; // Ainda tem tempo (30 dias)
        }

        return 'expiry-safe';
    };

    const formatExpiryDate = (expiryDate) => {
        if (!expiryDate) return 'Sem vencimento';

        const date = new Date(expiryDate);
        const now = new Date();
        const diffTime = date.getTime() - now.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays < 0) {
            return `Vencido há ${Math.abs(diffDays)} dias`;
        } else if (diffDays === 0) {
            return 'Vence hoje';
        } else if (diffDays === 1) {
            return 'Vence amanhã';
        } else {
            return `Vence em ${diffDays} dias`;
        }
    };

    const formatTriggerDays = (triggerDays) => {
        if (!triggerDays || triggerDays.length === 0) return 'Não configurado';
        return triggerDays.map(days => `${days} dia${days > 1 ? 's' : ''}`).join(', ');
    };

    const formatNextNotification = (nextNotification) => {
        if (!nextNotification) return 'Não agendado';

        const date = new Date(nextNotification);
        const now = new Date();
        const diffTime = date.getTime() - now.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays < 0) {
            return 'Atrasado';
        } else if (diffDays === 0) {
            return 'Hoje';
        } else if (diffDays === 1) {
            return 'Amanhã';
        } else {
            return `Em ${diffDays} dias`;
        }
    };

    // Ações dos alertas
    const handleHeaderAction = (action) => {
        const actionKey = typeof action === 'string' ? action : action.key;
        if (actionKey === 'create') {
            newAlertModalVisible.value = true;
        }
    };


    const editAlert = (alert) => {
        selectedAlert.value = { ...alert };
        editModalVisible.value = true;
    };

    const deleteAlert = (alert) => {
        selectedAlert.value = alert;
        deleteModalVisible.value = true;
    };

    const confirmDelete = () => {
        if (selectedAlert.value) {
            const alertIndex = alerts.value.findIndex(a => a.id === selectedAlert.value.id);
            if (alertIndex !== -1) {
                alerts.value.splice(alertIndex, 1);
                notify(`Alerta "${selectedAlert.value.name}" foi excluído com sucesso!`, 'success');
            }
        }
        closeDeleteModal();
    };

    const saveAlert = (alertData) => {
        const alertIndex = alerts.value.findIndex(a => a.id === alertData.id);
        if (alertIndex !== -1) {
            alerts.value[alertIndex] = {
                ...alertData,
                updatedAt: new Date().toISOString()
            };
            notify(`Alerta "${alertData.name}" foi atualizado com sucesso!`, 'success');
        }
        closeModal();
    };

    const createAlert = (alertsData) => {
        // alertsData pode ser um array de alertas ou um único alerta
        const alertsArray = Array.isArray(alertsData) ? alertsData : [alertsData];

        alertsArray.forEach((alertData, index) => {
            const newAlert = {
                ...alertData,
                id: alerts.value.length + index + 1,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
            };
            alerts.value.unshift(newAlert);
        });

        const message = alertsArray.length === 1
            ? `Alerta "${alertsArray[0].name}" foi criado com sucesso!`
            : `${alertsArray.length} alertas foram criados com sucesso!`;

        notify(message, 'success');
        closeModal();
    };

    const closeModal = () => {
        editModalVisible.value = false;
        newAlertModalVisible.value = false;
        selectedAlert.value = null;
    };

    const closeDeleteModal = () => {
        deleteModalVisible.value = false;
        selectedAlert.value = null;
    };

    // Lifecycle
    onMounted(() => {
        alerts.value = [...mockAutomationAlerts];
    });
</script>

<style lang="scss" scoped>
    @import '@/assets/css/mediaScreen.scss';
    @import '@/assets/css/components.scss';

    .automationAlerts {}

    // ===== SEÇÃO DE ESTATÍSTICAS =====
    .statsSection {
        margin-bottom: 1.5em;

        .statCard {
            background: white;
            border: 1px solid var(--borderColorLight, #e5e7eb);
            border-radius: 1.25em;
            padding: 1.25em;
            transition: all 0.3s ease;
            position: relative;
            overflow: hidden;
            height: 100%;
            display: flex;
            flex-direction: column;

            &::before {
                content: '';
                position: absolute;
                top: 0;
                left: -100%;
                width: 100%;
                height: 2px;
                background: linear-gradient(90deg, transparent, var(--linkColor), transparent);
                opacity: 0;
                transition: opacity 0.3s ease;
            }

            &:hover {
                box-shadow:
                    0 8px 32px rgba(0, 0, 0, 0.08),
                    0 1px 2px rgba(0, 0, 0, 0.05);
                border-color: rgba(59, 130, 246, 0.2);

                &::before {
                    opacity: 1;
                }
            }

            &_header {
                display: flex;
                align-items: flex-start;
                gap: 1em;
                margin-bottom: 0.75em;
                flex: 1;
            }

            &_icon {
                width: 3.25em;
                height: 3.25em;
                border-radius: 1em;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 1.125em;
                flex-shrink: 0;
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
                position: relative;

                &::after {
                    content: '';
                    position: absolute;
                    inset: 0;
                    border-radius: inherit;
                    background: linear-gradient(135deg, rgba(255,255,255,0.2), rgba(255,255,255,0.05));
                    pointer-events: none;
                }
            }

            &_info {
                flex: 1;
            }

            &_label {
                font-size: 0.875em;
                color: rgba(100, 116, 139, 0.8);
                font-weight: 600;
                margin-bottom: 0.5em;
                display: block;
                text-transform: uppercase;
                letter-spacing: 0.025em;
            }

            &_numbers {
                display: flex;
                align-items: baseline;
                gap: 0.5em;
                margin-bottom: 0.25em;
            }

            &_main {
                font-size: 2.25em;
                font-weight: 800;
                color: var(--textPrimary);
                line-height: 1;
            }

            &_secondary {
                font-size: 0.9em;
                color: rgba(100, 116, 139, 0.7);
                font-weight: 500;
            }

            // Variantes de cores
            &--primary {
                .statCard_icon {
                    background: linear-gradient(135deg, #3b82f6, #1d4ed8);
                    color: white;
                }
            }

            &--success {
                .statCard_icon {
                    background: linear-gradient(135deg, #10b981, #059669);
                    color: white;
                }
            }

            &--warning {
                .statCard_icon {
                    background: linear-gradient(135deg, #f59e0b, #d97706);
                    color: white;
                }
            }

            &--danger {
                .statCard_icon {
                    background: linear-gradient(135deg, #ef4444, #dc2626);
                    color: white;
                }
            }
        }
    }

    // ===== SEÇÃO DE ALERTAS =====
    .alertsSection {
.alertCard {
            position: relative;
            background: white;
            border-radius: 1.5em;
            padding: 0;
            margin-bottom: 2em;
            border: 1px solid var(--borderColorLight);
            transition: all 0.3s ease;
            overflow: hidden;

            &:hover {
                box-shadow: 0 8px 25px rgba(0, 0, 0, 0.12);
                border-color: var(--linkColor);
            }


.card-status-bar {
                height: 4px;
                width: 100%;

                &.active {
                    background: linear-gradient(90deg, #10b981, #059669);
                }


                &.urgent {
                    background: linear-gradient(90deg, #ef4444, #dc2626);
                }
            }

.card-header {
                padding: 1.75em 1.75em 1em;

                .alert-header-content {
                    display: flex;
                    flex-direction: row;
                    justify-content: space-between;
                    align-items: flex-start;
                    gap: 1em;
                    margin-bottom: 1em;

                    .alert-title-section {
                        flex: 1;

                        h4 {
                            font-size: 1.125em;
                            font-weight: 700;
                            color: var(--textPrimary);
                            margin: 0;
                            line-height: 1.4;
                        }
                    }

                    .alert-actions {
                        display: flex;
                        align-items: flex-start;
                        flex-shrink: 0;
                    }
                }

                .alert-description {
                    .document-info {
                        display: flex;
                        flex-direction: column;
                        gap: 0.75em;

                        .document-type {
                            display: flex;
                            align-items: center;
                            gap: 0.5em;
                            font-size: 0.875em;
                            color: var(--textSecondary);

                            .person-type-badge {
                                font-size: 0.75em;
                                font-weight: 600;
                                padding: 0.25em 0.5em;
                                border-radius: 0.375em;
                                text-transform: uppercase;

                                &.fisica {
                                    background: rgba(59, 130, 246, 0.1);
                                    color: #3b82f6;
                                }

                                &.juridica {
                                    background: rgba(168, 85, 247, 0.1);
                                    color: #8b5cf6;
                                }
                            }
                        }

                        .expiry-info {
                            font-size: 0.875em;
                            font-weight: 600;
                            padding: 0.375em 0.75em;
                            border-radius: 0.5em;
                            display: inline-flex;
                            align-self: flex-start;

                            &.expired {
                                background: rgba(239, 68, 68, 0.1);
                                color: #dc2626;
                            }

                            &.expiring {
                                background: rgba(245, 158, 11, 0.1);
                                color: #d97706;
                            }

                            &.safe {
                                background: rgba(16, 185, 129, 0.1);
                                color: #059669;
                            }
                        }
                    }
                }
            }

            .card-content {
                padding: 0 1.75em 1.75em;

                .alert-metrics {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 1em;

                    .detail-item {
                        display: flex;
                        align-items: center;
                        gap: 0.75em;
                        padding: 0.875em;
                        background: rgba(249, 250, 251, 0.8);
                        border: 1px solid var(--borderColorLight);
                        border-radius: 0.75em;

                        &--compact {
                            padding: 0.75em;
                        }

                        .detail-icon {
                            width: 2em;
                            height: 2em;
                            border-radius: 0.5em;
                            background: rgba(59, 130, 246, 0.1);
                            color: #3b82f6;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            font-size: 0.875em;
                            flex-shrink: 0;
                        }

                        .detail-content {
                            display: flex;
                            flex-direction: column;
                            gap: 0.125em;

                            .detail-label {
                                font-size: 0.75em;
                                color: var(--textSecondary);
                                font-weight: 500;
                                text-transform: uppercase;
                                letter-spacing: 0.05em;
                            }

                            .detail-value {
                                font-size: 0.875em;
                                color: var(--textPrimary);
                                font-weight: 600;
                            }
                        }
                    }
                }
            }

            .card-footer {
                padding: 1.25em 1.75em 1.75em;
                border-top: 1px solid var(--borderColorLight);
                margin-top: 0;

                .next-notification-info {
                    display: flex;
                    align-items: center;
                    gap: 0.75em;

                    .next-notification-icon {
                        width: 2em;
                        height: 2em;
                        border-radius: 0.5em;
                        background: linear-gradient(135deg, #3b82f6, #1d4ed8);
                        color: white;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        font-size: 0.875em;
                        flex-shrink: 0;
                    }

                    .next-notification-content {
                        display: flex;
                        flex-direction: column;
                        gap: 0.125em;

                        .next-notification-label {
                            font-size: 0.75em;
                            color: var(--textSecondary);
                            font-weight: 500;
                            text-transform: uppercase;
                            letter-spacing: 0.05em;
                        }

                        .next-notification-date {
                            font-size: 0.875em;
                            color: var(--textPrimary);
                            font-weight: 600;
                        }
                    }
                }
            }

            &_content {
                padding: 0 1.5em 1.5em 1.5em;
                flex: 1;
            }

            &_details {
                display: flex;
                flex-direction: column;
                gap: 0.75em;
            }

            &_detailItem {
                display: flex;
                justify-content: space-between;
                align-items: center;
            }

            &_detailLabel {
                font-size: 0.8125em;
                color: var(--textSecondary);
                font-weight: 500;
            }

            &_detailValue {
                font-size: 0.8125em;
                color: var(--textPrimary);
                font-weight: 600;
                text-align: right;
            }

            // Estados do card

            &--critical {
                border-color: rgba(239, 68, 68, 0.3);
            }

            &--warning {
                border-color: rgba(245, 158, 11, 0.3);
            }

            &--safe {
                border-color: rgba(16, 185, 129, 0.3);
            }
        }

.status-badge {
            display: flex;
            align-items: center;
            gap: 0.5em;
            font-size: 0.75em;
            font-weight: 600;
            padding: 0.5em 1em;
            border-radius: 1.5em;
            text-transform: uppercase;
            letter-spacing: 0.025em;

            .status-dot {
                width: 0.5em;
                height: 0.5em;
                border-radius: 50%;
                flex-shrink: 0;
            }

            &.active {
                background: rgba(16, 185, 129, 0.1);
                color: #059669;

                .status-dot {
                    background: #059669;
                }
            }


            &.urgent {
                background: rgba(239, 68, 68, 0.1);
                color: #dc2626;

                .status-dot {
                    background: #dc2626;
                }
            }
        }

.action-buttons {
            display: flex;
            gap: 0.5em;
        }

        .action-btn {
            width: 2.25em;
            height: 2.25em;
            border-radius: 0.75em;
            border: 1px solid var(--borderColorLight);
            background: white;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            transition: all 0.2s ease;
            font-size: 0.875em;

            &:hover {
                transform: scale(1.05);
                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
            }

            &--toggle {
                &.pause {
                    color: #f59e0b;
                    border-color: rgba(245, 158, 11, 0.3);

                    &:hover {
                        background: rgba(245, 158, 11, 0.1);
                    }
                }

                &.play {
                    color: #10b981;
                    border-color: rgba(16, 185, 129, 0.3);

                    &:hover {
                        background: rgba(16, 185, 129, 0.1);
                    }
                }
            }

            &--edit {
                color: #3b82f6;
                border-color: rgba(59, 130, 246, 0.3);

                &:hover {
                    background: rgba(59, 130, 246, 0.1);
                }
            }

            &--delete {
                color: #ef4444;
                border-color: rgba(239, 68, 68, 0.3);

                &:hover {
                    background: rgba(239, 68, 68, 0.1);
                    color: #dc2626;
                }
            }
        }
    }

    // ===== MODAL CONTENT =====
    .modalContent {
        padding: 1.5em 0;

        .confirmText {
            font-size: 1em;
            color: var(--textPrimary);
            margin-bottom: 1em;
            line-height: 1.5;
        }

        .warningText {
            font-size: 0.875em;
            color: var(--textSecondary);
            margin-bottom: 2em;
            line-height: 1.5;
        }

        .modalActions {
            display: flex;
            gap: 1em;
            justify-content: flex-end;
            padding-top: 1.5em;
            border-top: 1px solid var(--borderColorLight, #e5e7eb);
            margin-top: 1.5em;
        }
    }

    // ===== RESPONSIVIDADE =====
    @include screen_medias(s, m) {
        .automationAlerts {}

        .statsSection {
            .statCard {
                padding: 1.25em;

                &_header {
                    gap: 1em;
                    margin-bottom: 1em;
                }

                &_icon {
                    width: 2.75em;
                    height: 2.75em;
                    font-size: 1em;
                }

                &_main {
                    font-size: 1.875em;
                }
            }
        }

        .alertsSection {
            .alertCard {
                .card-header {
                    padding: 1.25em 1.25em 0.75em 1.25em;
                }

                .card-content {
                    padding: 0 1.25em 1.25em 1.25em;
                }

                &_titleSection h4 {
                    font-size: 1em;
                }
            }

            .actionButtons {
                gap: 0.375em;
            }

            .actionBtn {
                width: 2em;
                height: 2em;
                font-size: 0.8125em;
            }
        }

        .modalContent .modalActions {
            flex-direction: column;
            gap: 0.75em;
        }
    }

    @include screen_s {
        .statsSection {
            .statCard {
                padding: 1em;

                &_header {
                    gap: 0.875em;
                    margin-bottom: 0.75em;
                }

                &_icon {
                    width: 2.5em;
                    height: 2.5em;
                }

                &_main {
                    font-size: 1.75em;
                }
            }
        }

        .alertsSection {
            .alertCard {
                .card-header {
                    padding: 1em 1em 0.5em 1em;
                }

                .card-content {
                    padding: 0 1em 1em 1em;
                }

                &_actions {
                    flex-direction: column;
                    gap: 0.75em;
                    align-items: stretch;
                }

                &_titleSection {
                    margin-top: 0.5em;
                }
            }

            .statusBadge {
                align-self: flex-start;
            }

            .actionButtons {
                justify-content: flex-end;
            }
        }
    }
</style>